import type { DeviceDeploymentInfoV4 } from '@emma/api_client/types';
import {
  type DeviceTypeInfo,
  getCompatibleOsVersionsForDevice,
  parseOsVersionTag,
} from '@emma/core/device';
import { stableOSVersions } from '@emma/core/os';
import { Box, Flex, Text, Tooltip } from '@lego/klik-ui';

type ApplicationService = {
  state: string;
  exitCode: number;
};

export type TrafficLightColor = 'green' | 'yellow' | 'red' | 'gray';

type TrafficLightResult = {
  color: TrafficLightColor;
  label: string;
};

const YELLOW_STATES = new Set(['restarting', 'created', 'paused']);

const colorMap: Record<TrafficLightColor, string> = {
  green: 'success.500',
  yellow: 'yellow.500',
  red: 'error.500',
  gray: 'slate.300',
};

// --- Sub traffic light logic ---

export const getContainerStatus = (
  deployments: DeviceDeploymentInfoV4[] | undefined
): TrafficLightResult & { running: number; total: number } => {
  const activeDeployments = deployments?.filter((d) => d.isActive) ?? [];

  const allServices: ApplicationService[] = activeDeployments
    .filter((d) => d.applicationServices)
    .flatMap((deployment) => {
      try {
        return JSON.parse(deployment.applicationServices!) as ApplicationService[];
      } catch {
        return [];
      }
    });

  if (allServices.length === 0) {
    return { color: 'gray', label: 'No application status reported', running: 0, total: 0 };
  }

  const isHealthy = (s: ApplicationService) =>
    s.state === 'running' || (s.state === 'exited' && (s.exitCode === 0 || s.exitCode === 143));

  const running = allServices.filter(isHealthy).length;
  const total = allServices.length;

  const hasIssue = allServices.some(
    (s) => s.state === 'dead' || (s.state === 'exited' && s.exitCode !== 0 && s.exitCode !== 143)
  );
  if (hasIssue) {
    return {
      color: 'red',
      label: 'One or more containers have crashed or are dead',
      running,
      total,
    };
  }

  const allHealthy = allServices.every(isHealthy);
  if (allHealthy) {
    return { color: 'green', label: `${running}/${total} containers healthy`, running, total };
  }

  const isTransitioning = allServices.some((s) => YELLOW_STATES.has(s.state));
  if (isTransitioning) {
    return {
      color: 'yellow',
      label: `${running}/${total} running, some in transitional state`,
      running,
      total,
    };
  }

  return { color: 'yellow', label: `${running}/${total} running`, running, total };
};

export const getConnectivityStatus = (isConnected: boolean): TrafficLightResult => {
  if (isConnected) {
    return { color: 'green', label: 'Device is connected' };
  }
  return { color: 'red', label: 'Device is disconnected' };
};

export const getDeploymentStatus = (
  deployments: DeviceDeploymentInfoV4[] | undefined,
  fleetActiveDeploymentIds: Map<number, number | null>
): TrafficLightResult => {
  const activeDeployments = deployments?.filter((d) => d.isActive) ?? [];

  if (activeDeployments.length === 0) {
    return { color: 'gray', label: 'No active deployments' };
  }

  let allMatchAndSucceeded = true;
  let hasData = false;
  let hasFleetWithoutExpectedDeployment = false;

  for (const deployment of activeDeployments) {
    if (deployment.latestState === 'FAILED') {
      return { color: 'red', label: 'One or more deployments have failed' };
    }

    const expectedId = fleetActiveDeploymentIds.get(deployment.fleetId);
    if (expectedId === undefined) {
      // Fleet data not available
      continue;
    }
    hasData = true;
    if (expectedId === null) {
      // Fleet has no active deployment set
      hasFleetWithoutExpectedDeployment = true;
      allMatchAndSucceeded = false;
      continue;
    }
    if (deployment.deploymentId !== expectedId || deployment.latestState !== 'SUCCEEDED') {
      allMatchAndSucceeded = false;
    }
  }

  if (!hasData) {
    return { color: 'red', label: 'Fleet deployment data unavailable' };
  }

  if (allMatchAndSucceeded) {
    return { color: 'green', label: 'Running expected deployment' };
  }

  if (hasFleetWithoutExpectedDeployment) {
    return { color: 'gray', label: 'Fleet has no active deployment set' };
  }

  return { color: 'yellow', label: 'Running a different deployment than expected by fleet' };
};

const getMajorVersion = (version: string): string | undefined => version.split('.')[0];

export const getOsVersionStatus = (
  os: { version: string; desiredVersion: string } | undefined
): TrafficLightResult => {
  if (!os) {
    return { color: 'gray', label: 'No OS version data' };
  }

  const deviceMajor = getMajorVersion(os.version);
  if (!deviceMajor) {
    return { color: 'gray', label: 'Unable to parse OS version' };
  }

  const stableVersions = [...stableOSVersions];
  const majorHasStableRelease = stableVersions.some((v) => getMajorVersion(v) === deviceMajor);

  if (majorHasStableRelease) {
    return { color: 'green', label: `OS major version ${deviceMajor}.x is supported` };
  }

  return {
    color: 'red',
    label: `OS major version ${deviceMajor}.x is no longer supported`,
  };
};

export const getOsUpdateAvailable = (
  os: { version: string } | undefined,
  deviceType: DeviceTypeInfo | null | undefined
): { available: boolean; latestVersion: string | null } => {
  if (!os) {
    return { available: false, latestVersion: null };
  }

  // Filter stable versions to what this device can actually run, and to the
  // same variant (kiosk vs base) the device is currently running. Variants are
  // versioned independently, so a base device must not be offered a kiosk-only
  // release (and vice versa).
  const deviceIsKiosk = parseOsVersionTag(os.version).isKiosk;
  const compatibleVersions = getCompatibleOsVersionsForDevice(deviceType, [
    ...stableOSVersions,
  ]).filter((v) => parseOsVersionTag(v).isKiosk === deviceIsKiosk);

  if (compatibleVersions.length === 0) {
    return { available: false, latestVersion: null };
  }

  // Find the overall latest compatible version
  const parseVersion = (v: string) => v.match(/^(\d+\.\d+\.\d+)/)?.[1];

  const sortedVersions = compatibleVersions
    .map(parseVersion)
    .filter((v): v is string => v != null)
    .filter((v, i, arr) => arr.indexOf(v) === i) // deduplicate
    .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));

  const latestVersion = sortedVersions[0];
  const currentVersion = parseVersion(os.version);

  if (!currentVersion || !latestVersion) {
    return { available: false, latestVersion: null };
  }

  const isOutdated = currentVersion.localeCompare(latestVersion, undefined, { numeric: true }) < 0;
  return { available: isOutdated, latestVersion: isOutdated ? latestVersion : null };
};

// --- Aggregate logic ---

export const colorPriority: Record<TrafficLightColor, number> = {
  red: 0,
  yellow: 1,
  gray: 2,
  green: 3,
};

export const getAggregateColor = (colors: TrafficLightColor[]): TrafficLightColor => {
  if (colors.length === 0) return 'gray';
  return colors.reduce((worst, c) => (colorPriority[c] < colorPriority[worst] ? c : worst));
};

// --- Components ---

type TrafficLightDotProps = {
  color: TrafficLightColor;
  label: string;
  size?: string;
};

export const TrafficLightDot = ({ color, label, size = '16px' }: TrafficLightDotProps) => {
  return (
    <Tooltip label={label}>
      <Box
        aria-label={label}
        as="span"
        background={colorMap[color]}
        borderRadius="full"
        display="inline-block"
        flexShrink={0}
        height={size}
        minHeight={size}
        minWidth={size}
        role="img"
        width={size}
      />
    </Tooltip>
  );
};

type SubTrafficLightProps = {
  color: TrafficLightColor;
  label: string;
  description: string;
};

export const SubTrafficLight = ({ color, label, description }: SubTrafficLightProps) => {
  return (
    <Flex alignItems="center" gap={2}>
      <TrafficLightDot color={color} label={label} size="12px" />
      <Text color="slate.600" fontSize="xs">
        {description}
      </Text>
    </Flex>
  );
};
